import type {
	Client,
	RequestConfig,
	ResponseConfig,
	ResponseErrorConfig,
} from "@kubb/plugin-client/clients/axios";
import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosResponse,
} from "axios";

// Re-export Kubb types so generated code can import them from this module
export type { Client, RequestConfig, ResponseConfig, ResponseErrorConfig };

// Extend the AxiosRequestConfig interface to include our retry flag
declare module "axios" {
	interface InternalAxiosRequestConfig {
		_retry?: boolean;
	}
}

export interface ApiClientConfig {
	baseURL: string;
	getToken: () => Promise<string | null>;
	onError?: (error: AxiosError) => void;
	onResponse?: (response: AxiosResponse) => void;
	onRequestError?: (error: AxiosError) => void;
}

// Auth-token getter and observability callbacks are mutable globals that the
// module-level interceptors read on every request. Decoupling them from axios
// creation lets the axios instance exist from import time — earlier than the
// React tree can call `configureApiClient` — so route loaders that fire on the
// first render don't crash with "client not configured". The interceptors fall
// back to sending without auth if the getter isn't wired yet; the response
// interceptor's 401-refresh path then retries with a fresh token.
let globalGetToken: (() => Promise<string | null>) | null = null;
let globalOnError: ((error: AxiosError) => void) | null = null;
let globalOnResponse: ((response: AxiosResponse) => void) | null = null;
let globalOnRequestError: ((error: AxiosError) => void) | null = null;

// No default Content-Type: axios 1.x infers it per request (application/json
// for plain objects, multipart/form-data with boundary for FormData). Forcing
// application/json here made axios convert FormData bodies to JSON, breaking
// file uploads (voice transcription).
const apiClient: AxiosInstance = axios.create();

apiClient.interceptors.request.use(async (requestConfig) => {
	try {
		if (globalGetToken) {
			const token = await globalGetToken();
			if (token) {
				requestConfig.headers = requestConfig.headers || {};
				requestConfig.headers.Authorization = `Bearer ${token}`;
			}
		}
	} catch {
		// Token retrieval failed — proceed without it; the response interceptor's
		// 401 path will trigger a refresh attempt.
	}
	return requestConfig;
});

apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		globalOnResponse?.(response);
		return response;
	},
	async (error: AxiosError) => {
		const originalRequest = error.config;

		if (
			error.response?.status === 401 &&
			originalRequest &&
			!originalRequest._retry &&
			globalGetToken
		) {
			originalRequest._retry = true;
			try {
				const newToken = await globalGetToken();
				if (newToken && originalRequest.headers) {
					originalRequest.headers.Authorization = `Bearer ${newToken}`;
					return apiClient(originalRequest);
				}
			} catch {
				// Refresh failed — let the error propagate to the caller.
			}
		}

		globalOnError?.(error);
		globalOnRequestError?.(error);
		return Promise.reject(error);
	},
);

/**
 * Wire the API client with the baseURL, auth token getter, and observability
 * callbacks. Safe to call multiple times — later calls override earlier ones.
 * The axios instance and its interceptors already exist at module load, so
 * requests issued before this is called simply go without auth (the 401-retry
 * path picks them up once the getter is set).
 */
export function configureApiClient(config: ApiClientConfig): AxiosInstance {
	apiClient.defaults.baseURL = config.baseURL;
	globalGetToken = config.getToken;
	globalOnError = config.onError ?? null;
	globalOnResponse = config.onResponse ?? null;
	globalOnRequestError = config.onRequestError ?? null;
	return apiClient;
}

/**
 * Get the configured API client instance. The instance exists from module
 * load; callers don't need to wait for `configureApiClient` before referencing
 * it. Requests before configure runs go without auth.
 */
export function getApiClient(): AxiosInstance {
	return apiClient;
}

// Internal config storage for the Kubb client wrapper
let internalConfig: Partial<RequestConfig<unknown>> = {};

/**
 * Custom fetch function that uses our configured axios instance.
 * Pass this to Kubb-generated hooks/clients via the `client` option.
 *
 * This function is typed with `typeof import("@kubb/plugin-client/clients/axios").default`
 * to ensure full compatibility with Kubb-generated code.
 *
 * @example
 * const { data } = useGetPaddocks(orgId, estId, {
 *   client: { client: apiClientFetch }
 * });
 */
async function apiClientFetchFn<TData, _TError = unknown, TVariables = unknown>(
	config: RequestConfig<TVariables>,
): Promise<{
	data: TData;
	status: number;
	statusText: string;
	headers: AxiosResponse["headers"];
}> {
	const mergedConfig = { ...internalConfig, ...config };
	const response = await apiClient.request<TData>({
		...mergedConfig,
		url: mergedConfig.url,
		data: mergedConfig.data as TVariables,
	});
	return {
		data: response.data,
		status: response.status,
		statusText: response.statusText,
		headers: response.headers,
	};
}

// Attach getConfig and setConfig to match Kubb's expected interface
export const apiClientFetch = Object.assign(apiClientFetchFn, {
	getConfig: () => internalConfig,
	setConfig: (config: RequestConfig) => {
		internalConfig = { ...internalConfig, ...config };
		return internalConfig;
	},
}) as typeof import("@kubb/plugin-client/clients/axios").default;

/**
 * Get the base URL for the API. Returns the current `defaults.baseURL` on
 * the axios instance — empty string until `configureApiClient` runs.
 */
export function getBaseURL(): string {
	return (apiClient.defaults.baseURL ?? "") as string;
}

/**
 * Default export for Kubb-generated code.
 * Kubb's importPath expects a default export matching the
 * `@kubb/plugin-client/clients/axios` interface.
 */
export default apiClientFetch;

/**
 * Export the axios instance for use by generated Kubb client code.
 */
export { apiClient };
